import React, { useState, useEffect, useContext, useMemo } from "react";
import { useTheme, alpha } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import {
  getAllMarcas,
  addMarca,
  updateMarcaEstado,
} from "../../api/Marcas/marcaApi";
import {
  Box,
  Typography,
  TextField,
  Paper,
  Alert,
  MenuItem,
  Switch,
  CircularProgress,
  Stack,
  Chip,
  Button,
  Skeleton,
  InputAdornment,
  useMediaQuery,
} from "@mui/material";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";

import { SidebarContext } from "../Sidebar/SidebarProvider";
import {
  getEntityInitial,
  getClientColor,
} from "../CriticalPathHorizontal/criticalPathAvatars";
import {
  pageHeroShellSx,
  pageContentCardSx,
  pageFieldSx,
  pageKpiChipSx,
  pagePrimaryButtonSx,
  pageMobileCardSx,
  pageSectionTitleSx,
} from "../../styles/pageModernStyles";

const GestionMarcas = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { t } = useTranslation();
  const { contentMargin } = useContext(SidebarContext);
  const accent = theme.palette.primary.main;

  const [marcas, setMarcas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMarca, setNewMarca] = useState("");
  const [errorKey, setErrorKey] = useState(null);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);

  const pageGradient =
    theme.palette.mode === "dark"
      ? `linear-gradient(180deg, ${alpha(theme.palette.background.default, 1)} 0%, ${alpha(theme.palette.primary.main, 0.06)} 100%)`
      : "linear-gradient(180deg, #f8fafc 0%, #eef6f8 45%, #f5f3ff 100%)";

  const fieldSx = useMemo(
    () => ({
      ...pageFieldSx(theme),
      minWidth: 0,
      width: "100%",
    }),
    [theme],
  );

  const filterChipSx = (active, paletteKey = "primary") => ({
    height: 30,
    fontWeight: 700,
    fontSize: "0.72rem",
    borderRadius: 999,
    ...(active
      ? {
          bgcolor: alpha(theme.palette[paletteKey].main, 0.14),
          color: theme.palette[paletteKey].dark,
          border: "1px solid",
          borderColor: alpha(theme.palette[paletteKey].main, 0.35),
        }
      : {
          bgcolor: alpha(theme.palette.background.paper, 0.85),
          color: theme.palette.text.secondary,
          border: "1px solid",
          borderColor: alpha(theme.palette.divider, 0.85),
        }),
  });

  const fetchMarcas = async () => {
    try {
      const data = await getAllMarcas();
      setMarcas(data);
    } catch (e) {
      console.error("Error al cargar marcas:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMarcas();
  }, []);

  const stats = useMemo(
    () => ({
      total: marcas.length,
      active: marcas.filter((m) => m.ACTIVO === "S").length,
      inactive: marcas.filter((m) => m.ACTIVO === "N").length,
    }),
    [marcas],
  );

  const filteredMarcas = useMemo(() => {
    const searchLower = search.trim().toLowerCase();
    let list = marcas;

    if (filter === "active") {
      list = list.filter((m) => m.ACTIVO === "S");
    } else if (filter === "inactive") {
      list = list.filter((m) => m.ACTIVO === "N");
    }

    if (searchLower) {
      list = list.filter((m) =>
        String(m.MARCA || "")
          .toLowerCase()
          .includes(searchLower),
      );
    }

    return [...list].sort((a, b) =>
      String(a.MARCA || "").localeCompare(String(b.MARCA || ""), undefined, {
        sensitivity: "base",
      }),
    );
  }, [marcas, filter, search]);

  const handleAddMarca = async () => {
    if (!newMarca.trim()) {
      setErrorKey("empty");
      return;
    }

    setErrorKey(null);
    setSaving(true);
    try {
      await addMarca({ MARCA: newMarca.trim(), ACTIVO: "S" });
      setNewMarca("");
      await fetchMarcas();
    } catch (error) {
      if (error.response && error.response.status === 409) {
        setErrorKey("exists");
      } else {
        setErrorKey("generic");
      }
    } finally {
      setSaving(false);
    }
  };

  const errorMessage =
    errorKey == null
      ? null
      : t(
          {
            empty: "GestionMarcasPage.errorEmpty",
            exists: "GestionMarcasPage.errorExists",
            generic: "GestionMarcasPage.errorGeneric",
          }[errorKey],
        );

  const handleEstadoChange = async (id, currentEstado) => {
    try {
      const newEstado = currentEstado === "S" ? "N" : "S";
      await updateMarcaEstado(id, newEstado);
      await fetchMarcas();
    } catch (e) {
      console.error("Error al actualizar estado:", e);
    }
  };

  const renderBrandRow = (marca) => {
    const isActive = marca.ACTIVO === "S";
    const brandColor = getClientColor(marca.MARCA);

    return (
      <Box
        key={marca.ID_MARCA}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          px: { xs: 0, md: 1.5 },
          py: { xs: 0, md: 1.15 },
          borderRadius: 2,
          transition: "background-color 0.15s ease",
          "&:hover": {
            bgcolor: alpha(theme.palette.primary.main, 0.04),
          },
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 800,
            fontSize: "0.75rem",
            color: "#fff",
            bgcolor: brandColor,
            flexShrink: 0,
            boxShadow: `0 2px 8px ${alpha(brandColor, 0.35)}`,
          }}
        >
          {getEntityInitial(marca.MARCA)}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "0.9rem",
              lineHeight: 1.3,
              color: "text.primary",
            }}
            noWrap
          >
            {marca.MARCA}
          </Typography>
          <Chip
            label={
              isActive
                ? t("GestionMarcasPage.statusOn")
                : t("GestionMarcasPage.statusOff")
            }
            size="small"
            sx={{
              mt: 0.35,
              height: 20,
              fontSize: "0.65rem",
              fontWeight: 700,
              bgcolor: isActive
                ? alpha(theme.palette.success.main, 0.12)
                : alpha(theme.palette.text.secondary, 0.1),
              color: isActive ? "success.dark" : "text.secondary",
            }}
          />
        </Box>

        <Switch
          checked={isActive}
          onChange={() => handleEstadoChange(marca.ID_MARCA, marca.ACTIVO)}
          color="primary"
          inputProps={{
            "aria-label": isActive
              ? t("GestionMarcasPage.statusOn")
              : t("GestionMarcasPage.statusOff"),
          }}
        />
      </Box>
    );
  };

  const renderLoading = () =>
    isMobile ? (
      <Stack spacing={1.25}>
        {[...Array(5)].map((_, i) => (
          <Skeleton
            key={i}
            variant="rounded"
            height={72}
            sx={{ borderRadius: 2.5 }}
          />
        ))}
      </Stack>
    ) : (
      <Stack spacing={0.5}>
        {[...Array(6)].map((_, i) => (
          <Skeleton key={i} variant="rounded" height={52} sx={{ borderRadius: 2 }} />
        ))}
      </Stack>
    );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100dvh",
        boxSizing: "border-box",
        overflow: "auto",
        background: pageGradient,
        pt: { xs: 1, md: 1.25 },
        px: { xs: 1.25, sm: 2 },
        pr: { xs: 1.25, sm: 2, md: 3 },
        pb: { xs: 10, md: 2 },
        ...contentMargin,
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 820,
          mx: "auto",
          flex: 1,
          minWidth: 0,
        }}
      >
        {/* Hero */}
        <Box sx={{ ...pageHeroShellSx(theme, { sticky: !isMobile }), mb: 1.5 }}>
          <Stack spacing={{ xs: 1.25, md: 1 }} sx={{ p: { xs: 1.25, md: 1.5 } }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              alignItems={{ md: "center" }}
              justifyContent="space-between"
              spacing={{ xs: 1.25, md: 1 }}
            >
              <Stack
                direction="row"
                spacing={1.25}
                alignItems="flex-start"
                sx={{ minWidth: 0, flex: 1 }}
              >
                <Box
                  sx={{
                    width: { xs: 44, md: 40 },
                    height: { xs: 44, md: 40 },
                    borderRadius: 2,
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: alpha(accent, 0.14),
                    color: theme.palette.primary.dark,
                  }}
                >
                  <StorefrontOutlinedIcon sx={{ fontSize: { xs: 22, md: 20 } }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 800,
                      lineHeight: 1.15,
                      fontSize: { xs: "1.05rem", md: "1.12rem" },
                    }}
                  >
                    {t("sidebar.brands")}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mt: 0.35,
                      display: { xs: "block", md: "none" },
                      fontSize: "0.8rem",
                      lineHeight: 1.4,
                    }}
                  >
                    {t("GestionMarcasPage.heroSubtitle")}
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={0.5}
                    useFlexGap
                    flexWrap="wrap"
                    sx={{ mt: 0.5, rowGap: 0.35 }}
                  >
                    <Chip
                      label={`${stats.total} ${t("GestionMarcasPage.totalBrands", { defaultValue: "marcas" })}`}
                      size="small"
                      variant="outlined"
                      sx={pageKpiChipSx}
                    />
                    <Chip
                      label={`${stats.active} ${t("GestionMarcasPage.active")}`}
                      size="small"
                      sx={{
                        ...pageKpiChipSx,
                        bgcolor: alpha(theme.palette.success.main, 0.1),
                        color: theme.palette.success.dark,
                      }}
                    />
                    {stats.inactive > 0 ? (
                      <Chip
                        label={`${stats.inactive} ${t("GestionMarcasPage.inactive")}`}
                        size="small"
                        sx={{
                          ...pageKpiChipSx,
                          bgcolor: alpha(theme.palette.text.secondary, 0.1),
                          color: "text.secondary",
                        }}
                      />
                    ) : null}
                  </Stack>
                </Box>
              </Stack>

              <TextField
                size="small"
                placeholder={t("GestionMarcasPage.searchPlaceholder", {
                  defaultValue: "Buscar marca…",
                })}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{
                  ...fieldSx,
                  width: { xs: "100%", md: 240 },
                  flexShrink: 0,
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchOutlinedIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>

            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
              {[
                { value: "all", label: t("GestionMarcasPage.all") },
                { value: "active", label: t("GestionMarcasPage.active") },
                { value: "inactive", label: t("GestionMarcasPage.inactive") },
              ].map((opt) => (
                <Chip
                  key={opt.value}
                  label={opt.label}
                  clickable
                  onClick={() => {
                    setFilter(opt.value);
                    setErrorKey(null);
                  }}
                  sx={filterChipSx(filter === opt.value)}
                />
              ))}
            </Stack>
          </Stack>
        </Box>

        {errorMessage ? (
          <Alert
            severity="error"
            sx={{ mb: 1.5, borderRadius: 2 }}
            onClose={() => setErrorKey(null)}
          >
            {errorMessage}
          </Alert>
        ) : null}

        {/* Alta + listado */}
        <Box sx={{ ...pageContentCardSx(theme, accent), p: { xs: 1.5, md: 2 } }}>
          <Typography sx={{ ...pageSectionTitleSx, mb: 1.25 }}>
            {t("GestionMarcasPage.addBrand")}
          </Typography>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            alignItems={{ sm: "flex-start" }}
            sx={{ mb: 2 }}
          >
            <TextField
              label={t("GestionMarcasPage.newBrand")}
              value={newMarca}
              onChange={(e) => {
                setNewMarca(e.target.value);
                if (errorKey) setErrorKey(null);
              }}
              fullWidth
              size="small"
              variant="outlined"
              onKeyDown={(e) => {
                if (e.key === "Enter") handleAddMarca();
              }}
              sx={fieldSx}
            />
            <Button
              variant="contained"
              onClick={handleAddMarca}
              disabled={saving || !newMarca.trim()}
              startIcon={
                saving ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <AddOutlinedIcon />
                )
              }
              sx={{
                ...pagePrimaryButtonSx(theme),
                px: 2.5,
                whiteSpace: "nowrap",
                width: { xs: "100%", sm: "auto" },
                flexShrink: 0,
              }}
            >
              {t("GestionMarcasPage.addBrand")}
            </Button>
          </Stack>

          <Typography sx={{ ...pageSectionTitleSx, mb: 1 }}>
            {t("GestionMarcasPage.brandList", { defaultValue: "Marcas" })}
            {!loading ? (
              <Typography
                component="span"
                sx={{
                  ml: 0.75,
                  fontWeight: 600,
                  fontSize: "0.68rem",
                  color: "text.disabled",
                }}
              >
                ({filteredMarcas.length})
              </Typography>
            ) : null}
          </Typography>

          {loading ? (
            renderLoading()
          ) : filteredMarcas.length === 0 ? (
            <Paper
              variant="outlined"
              sx={{
                py: 4,
                px: 2,
                textAlign: "center",
                borderRadius: 2.5,
                borderStyle: "dashed",
                bgcolor: alpha(theme.palette.background.paper, 0.6),
              }}
            >
              <StorefrontOutlinedIcon
                sx={{ fontSize: 36, color: "action.disabled", mb: 1 }}
              />
              <Typography color="text.secondary" fontWeight={600}>
                {search.trim()
                  ? t("GestionMarcasPage.noSearchResults", {
                      defaultValue: "No hay marcas con esa búsqueda",
                    })
                  : filter === "active"
                    ? t("GestionMarcasPage.noActive", {
                        defaultValue: "No hay marcas activas",
                      })
                    : filter === "inactive"
                      ? t("GestionMarcasPage.noInactive", {
                          defaultValue: "No hay marcas inactivas",
                        })
                      : t("GestionMarcasPage.noBrands", {
                          defaultValue: "Aún no hay marcas registradas",
                        })}
              </Typography>
            </Paper>
          ) : isMobile ? (
            <Stack spacing={1.25}>
              {filteredMarcas.map((marca) => (
                <Box
                  key={marca.ID_MARCA}
                  sx={pageMobileCardSx(theme, {
                    active: marca.ACTIVO === "S",
                    accentColor:
                      marca.ACTIVO === "S"
                        ? theme.palette.success.main
                        : theme.palette.text.secondary,
                  })}
                >
                  {renderBrandRow(marca)}
                </Box>
              ))}
            </Stack>
          ) : (
            <Paper
              variant="outlined"
              sx={{
                borderRadius: 2.5,
                overflow: "hidden",
                borderColor: alpha(theme.palette.divider, 0.75),
              }}
            >
              <Stack
                divider={
                  <Box
                    sx={{
                      height: "1px",
                      bgcolor: alpha(theme.palette.divider, 0.6),
                      mx: 1.5,
                    }}
                  />
                }
              >
                {filteredMarcas.map((marca) => renderBrandRow(marca))}
              </Stack>
            </Paper>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default GestionMarcas;
